import React, { useEffect, useState } from 'react';
import {
  Box, Typography, Button, CircularProgress, Alert,
  Table, TableBody, TableCell, TableHead, TableRow, Divider, Chip,
} from '@mui/material';
import { Print as PrintIcon, ArrowBack as BackIcon } from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import millingApi from '../../../services/millingApi';

const SPEC: Record<string, Record<string, { min?: number; max?: number; unit: string }>> = {
  moisture:        { grain_lot: { max: 14, unit: '%' },      finished_product: { max: 14.5, unit: '%' } },
  protein:         { grain_lot: { min: 11, unit: '%' },      finished_product: { min: 10.5, unit: '%' } },
  ash:             { grain_lot: { max: 1.8, unit: '%' },     finished_product: { max: 0.6, unit: '%' } },
  fallingNumber:   { grain_lot: { min: 200, unit: 's' },     finished_product: { min: 250, unit: 's' } },
  wetGluten:       { grain_lot: { min: 25, unit: '%' },      finished_product: { min: 25, unit: '%' } },
  waterAbsorption: { finished_product: { min: 56, unit: '%' } },
  stability:       { finished_product: { min: 5, unit: 'min' } },
  aflatoxin:       { grain_lot: { max: 10, unit: 'ppb' },    finished_product: { max: 4, unit: 'ppb' } },
  foreignMatter:   { grain_lot: { max: 0.5, unit: '%' } },
};

function checkSpec(key: string, sampleType: string, value: number | null): 'pass' | 'fail' | 'na' {
  if (value == null) return 'na';
  const s = SPEC[key]?.[sampleType];
  if (!s) return 'na';
  if (s.min != null && value < s.min) return 'fail';
  if (s.max != null && value > s.max) return 'fail';
  return 'pass';
}

function fmtSpec(key: string, sampleType: string): string {
  const s = SPEC[key]?.[sampleType];
  if (!s) return '-';
  const parts = [];
  if (s.min != null) parts.push(`≥${s.min}${s.unit}`);
  if (s.max != null) parts.push(`≤${s.max}${s.unit}`);
  return parts.join(', ');
}

const ROWS: { key: string; label: string; unit: string }[] = [
  { key: 'moisture', label: 'Moisture', unit: '%' },
  { key: 'protein', label: 'Protein', unit: '%' },
  { key: 'ash', label: 'Ash', unit: '%' },
  { key: 'fallingNumber', label: 'Falling Number', unit: 's' },
  { key: 'wetGluten', label: 'Wet Gluten', unit: '%' },
  { key: 'testWeight', label: 'Test Weight', unit: 'kg/hl' },
  { key: 'waterAbsorption', label: 'Water Absorption', unit: '%' },
  { key: 'developmentTime', label: 'Development Time', unit: 'min' },
  { key: 'stability', label: 'Stability', unit: 'min' },
  { key: 'whitenessIndex', label: 'Whiteness Index', unit: '' },
  { key: 'aflatoxin', label: 'Aflatoxin', unit: 'ppb' },
  { key: 'foreignMatter', label: 'Foreign Matter', unit: '%' },
  { key: 'damagedKernels', label: 'Damaged Kernels', unit: '%' },
];

const sampleTypeLabels: Record<string, string> = {
  grain_lot: 'Grain Lot',
  tempering: 'Tempering',
  finished_product: 'Finished Product',
};

const CoaViewer: React.FC = () => {
  const navigate = useNavigate();
  const { referenceId } = useParams<{ referenceId: string }>();
  const [coa, setCoa] = useState<{ referenceId: string; tests: any[] } | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    millingApi.getCoa(referenceId!)
      .then(setCoa)
      .catch(() => setError('Failed to load CoA data.'))
      .finally(() => setLoading(false));
  }, [referenceId]);

  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}><CircularProgress /></Box>;
  if (error) return <Alert severity="error" sx={{ m: 3 }}>{error}</Alert>;
  if (!coa || coa.tests.length === 0) {
    return (
      <Box sx={{ p: 3 }}>
        <Button startIcon={<BackIcon />} onClick={() => navigate('/milling/quality-tests')}>Back</Button>
        <Alert severity="info" sx={{ mt: 2 }}>No quality tests found for this reference.</Alert>
      </Box>
    );
  }

  const today = new Date().toLocaleDateString();
  const firstTest = coa.tests[0];

  return (
    <Box>
      {/* Toolbar — hidden on print */}
      <Box sx={{ p: 2, display: 'flex', gap: 2, displayPrint: 'none' }} className="no-print">
        <Button startIcon={<BackIcon />} onClick={() => navigate('/milling/quality-tests')}>Back</Button>
        <Button variant="contained" startIcon={<PrintIcon />} onClick={() => window.print()}>
          Print / Export PDF
        </Button>
      </Box>

      {/* CoA Document */}
      <Box
        sx={{
          maxWidth: 900,
          mx: 'auto',
          p: 4,
          bgcolor: 'white',
          '@media print': { p: 2, maxWidth: '100%' },
        }}
      >
        {/* Header */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
          <Box>
            <Typography variant="h4" fontWeight="bold">Certificate of Analysis</Typography>
            <Typography variant="body2" color="text.secondary">Flour Milling — Quality Report</Typography>
          </Box>
          <Box sx={{ textAlign: 'right' }}>
            <Typography variant="body2"><strong>Date Issued:</strong> {today}</Typography>
            <Typography variant="body2"><strong>Reference:</strong> {firstTest.referenceNumber || referenceId}</Typography>
            {firstTest.productName && (
              <Typography variant="body2"><strong>Product:</strong> {firstTest.productName}</Typography>
            )}
          </Box>
        </Box>

        <Divider sx={{ mb: 3 }} />

        {/* Summary */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="h6" gutterBottom>Test Summary</Typography>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            <Typography variant="body2"><strong>Total Tests:</strong> {coa.tests.length}</Typography>
            <Typography variant="body2">
              <strong>Passed:</strong> {coa.tests.filter(t => t.status === 'passed' || t.status === 'approved').length}
            </Typography>
            <Typography variant="body2">
              <strong>Failed:</strong> {coa.tests.filter(t => t.status === 'failed').length}
            </Typography>
            <Typography variant="body2">
              <strong>Approved:</strong> {coa.tests.filter(t => t.status === 'approved').length}
            </Typography>
          </Box>
        </Box>

        {/* One table per test */}
        {coa.tests.map((test, idx) => (
          <Box key={test.id} sx={{ mb: 4 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
              <Typography variant="h6">
                Test {idx + 1}: {test.testNumber}
                <Typography component="span" variant="body2" color="text.secondary" sx={{ ml: 1 }}>
                  ({sampleTypeLabels[test.sampleType] || test.sampleType})
                </Typography>
              </Typography>
              <Chip
                label={test.status.toUpperCase()}
                size="small"
                color={test.status === 'approved' ? 'primary' : test.status === 'passed' ? 'success' : test.status === 'failed' ? 'error' : 'warning'}
              />
            </Box>
            <Box sx={{ display: 'flex', gap: 4, mb: 1, flexWrap: 'wrap' }}>
              <Typography variant="body2"><strong>Sample Date:</strong> {test.sampleDate}</Typography>
              {test.testedBy && <Typography variant="body2"><strong>Tested By:</strong> {test.testedBy}</Typography>}
              {test.approvedBy && <Typography variant="body2"><strong>Approved By:</strong> {test.approvedBy}</Typography>}
            </Box>

            <Table size="small" sx={{ border: '1px solid #e0e0e0' }}>
              <TableHead>
                <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                  <TableCell><strong>Parameter</strong></TableCell>
                  <TableCell><strong>Result</strong></TableCell>
                  <TableCell><strong>Specification</strong></TableCell>
                  <TableCell><strong>Compliance</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {ROWS.map(({ key, label, unit }) => {
                  const value = test[key];
                  if (value == null) return null;
                  const result = checkSpec(key, test.sampleType, Number(value));
                  const specStr = fmtSpec(key, test.sampleType);
                  return (
                    <TableRow key={key}>
                      <TableCell>{label}</TableCell>
                      <TableCell>{Number(value).toFixed(key === 'fallingNumber' ? 0 : 2)}{unit ? ` ${unit}` : ''}</TableCell>
                      <TableCell sx={{ color: 'text.secondary' }}>{specStr}</TableCell>
                      <TableCell>
                        {result === 'pass' && <Chip label="PASS" size="small" color="success" />}
                        {result === 'fail' && <Chip label="FAIL" size="small" color="error" />}
                        {result === 'na' && <Typography variant="caption" color="text.secondary">–</Typography>}
                      </TableCell>
                    </TableRow>
                  );
                })}
                {test.granulation && (
                  <TableRow>
                    <TableCell>Granulation</TableCell>
                    <TableCell>{test.granulation}</TableCell>
                    <TableCell sx={{ color: 'text.secondary' }}>–</TableCell>
                    <TableCell><Typography variant="caption" color="text.secondary">–</Typography></TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>

            {test.notes && (
              <Typography variant="body2" sx={{ mt: 1, color: 'text.secondary' }}>
                <strong>Notes:</strong> {test.notes}
              </Typography>
            )}
          </Box>
        ))}

        <Divider sx={{ my: 3 }} />

        {/* Signature block */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4 }}>
          {['Quality Manager', 'Production Manager', 'Authorized Signatory'].map((role) => (
            <Box key={role} sx={{ textAlign: 'center', minWidth: 160 }}>
              <Box sx={{ borderTop: '1px solid #333', pt: 1, mt: 4 }}>
                <Typography variant="body2">{role}</Typography>
              </Box>
            </Box>
          ))}
        </Box>

        <Typography variant="caption" display="block" sx={{ mt: 3, color: 'text.secondary', textAlign: 'center' }}>
          This certificate is generated by the MES Flour Milling module. Results are valid for the sample tested only.
        </Typography>
      </Box>

      <style>{`@media print { .no-print { display: none !important; } }`}</style>
    </Box>
  );
};

export default CoaViewer;
